/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SelectionModel, emptySelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { FocusedCell, GridLayout, GridComponent } from '@gh/core-ui-grid';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent } from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { getJeComponentId, JeComponent } from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  JeFocusedTable,
  queryJeComponentLayout,
  queryJeComponents,
  queryJeEditable,
  queryJeFocusedCell,
  queryJeSelectedComponentId,
} from '../je-details.state';
import { JeDetailsComponentGridDefinition } from './je-details-component-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-je-details-component-list',
  templateUrl: 'je-details-component-list.component.html',
  styleUrls: ['je-details-component-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsComponentGridDefinition],
})
export class JeDetailsComponentListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('grid') grid: GridComponent;

  private readonly destroy$ = new Subject<void>();

  selectedComponentId$: Observable<Maybe<ID>>;
  components$: Observable<JeComponent[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  editable$: Observable<boolean>;
  sourceSelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              public gridDefinition: JeDetailsComponentGridDefinition,
              private cd: ChangeDetectorRef,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    this.gridDefinition.bind(this.state.project(queryJeComponentLayout));

    this.components$ = this.state.select(queryJeComponents);
    this.selectedComponentId$ = this.state.select(queryJeSelectedComponentId);
    this.focusedCell$ = this.state.select(queryJeFocusedCell, JeFocusedTable.ComponentList);
    this.editable$ = this.state.select(queryJeEditable);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.api?.sizeColumnsToFit();
        window.dispatchEvent(new Event('resize'));
      }
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAdd(): void {
    this.commands.componentAdd();
  }

  onDelete(component: JeComponent): void {
    const id = getJeComponentId(component);
    this.commands.componentRemove(component).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const components = this.state.project(queryJeComponents) || [];
      const next = selectionModelAfterRowRemoved(
        this.sourceSelectionModel,
        id,
        components.length === 0,
      );
      if (next !== this.sourceSelectionModel) {
        this.onComponentSelectionChanged(next);
      }
    });
  }

  onCloneComponent(component: JeComponent): void {
    this.commands.componentClone(component);
  }

  onSelectedComponentChanged(id?: ID): void {
    this.commands.componentSelect(id);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onCellDoubleClicked({ colDef, data }: CellDoubleClickedEvent): void {
    if (this.state.project(queryJeEditable) && colDef.colId === 'variation') {
      this.commands.componentConfirmVariation(getJeComponentId(data));
    }
  }

  onLayoutChanged(layout: GridLayout): void {
    this.commands.layoutUpdate(layout);
  }

  onComponentSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
  }

  getSelectedComponents(): Observable<JeComponent[]> {
    return this.components$.pipe(
      map$((components) => {
        if (!components || components.length === 0) {
          return [];
        }

        if (this.sourceSelectionModel.isExcludeMode) {
          return components.filter((component) => !this.sourceSelectionModel.idList.includes(getJeComponentId(component)));
        } else {
          return components.filter((component) => this.sourceSelectionModel.idList.includes(getJeComponentId(component)));
        }
      }));
  }

  hasSelectedComponents(): Observable<boolean> {
    return this.getSelectedComponents().pipe(
      map$((selectedComponents) => selectedComponents.length > 0));
  }

  onDeleteSelected(): void {
    this.getSelectedComponents().pipe(
      take$(1),
      filter$((selectedComponents) => selectedComponents.length > 0),
      switchMap$((selectedComponents) => {
        const count = selectedComponents.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_component', { code: selectedComponents[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_components', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedComponents));
      }),
    ).subscribe((selectedComponents) => {
      this.commands.removeComponents(selectedComponents.map((component) => getJeComponentId(component)));
      this.sourceSelectionModel = emptySelectionModel();
      this.onComponentSelectionChanged(this.sourceSelectionModel);
    });
  }
}
